import * as React from "react"
import ReactDOM from "react-dom"
import zIndexes from "@digits-shared/DesignSystem/zIndexes"
import stringHelper from "@digits-shared/helpers/stringHelper"
import { useEscapeKeyCapture } from "@digits-shared/hooks/useEscapeKeyCapture"
import { useModalRoot } from "@digits-shared/hooks/useModalRoot"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import {
  AccountantItems,
  AccountingOrgsList,
} from "src/frontend/components/Shared/NavSidebar/Menu/AccountantItems"
import { MenuOtherLinks } from "src/frontend/components/Shared/NavSidebar/Menu/MenuOtherLinks"
import { OperatorItems } from "src/frontend/components/Shared/NavSidebar/Menu/OperatorItems"
import { SIDEBAR_WIDTH } from "src/frontend/components/Shared/NavSidebar/sidebarConstants"
import type FrontendSession from "src/frontend/session"
import { Experience } from "src/frontend/session/personas"

/*
  STYLES
*/

const MenuContainer = styled.div<{ top: number }>`
  display: flex;
  align-items: center;
  flex-direction: column;
  font-size: 14px;

  transition: opacity 505ms;
  opacity: 1;
  @starting-style {
    opacity: 0;
  }

  position: absolute;
  left: 16px;
  top: ${({ top }) => top + 4}px;
  z-index: ${zIndexes.sidebarMenu};
  // do not set overflow or the org-picker listbox will get trapped in Safari
  max-height: calc(100vh - 120px);
  width: ${SIDEBAR_WIDTH}px;

  border-radius: 8px;
  background: ${colors.white};
  box-shadow: 0 0 20px 0 ${colors.translucentSecondary20};

  &:focus-visible {
    outline: none;
  }
`

const MenuMainContent = styled.div`
  display: contents;

  // when the viewport is substantially small, we scroll the entire menu
  // rather than just scrolling the entity list, otherwise there are issues
  // with overflow and multiple scrollbars
  @media (max-height: 600px) {
    width: 100%;
    display: block;
    overflow-y: auto;
    overflow-x: hidden;
  }
`

/*
  INTERFACES
 */

interface SidebarMenuProps {
  headerTop: number
  isOpen: boolean
  hideMenu: () => void
  isShareUser?: boolean
  className?: string
}

/*
  COMPONENT
*/

export const SidebarMenu: React.FCWithRef<HTMLDivElement, SidebarMenuProps> = ({
  ref,
  headerTop,
  isOpen,
  className,
  hideMenu,
  isShareUser,
}) => {
  const modalRoot = useModalRoot()
  const showOrgPicker = useShowOrgPicker()

  React.useEffect(() => {
    if (isOpen && ref?.current) {
      // Small delay to ensure the portal is fully rendered
      const timeoutId = setTimeout(() => {
        ref?.current?.focus()
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [isOpen, ref])

  if (!isOpen) return null

  const classNames = stringHelper.classNames(className, isOpen && "business-expand")
  return ReactDOM.createPortal(
    // menu focus capture does not work unless tabIndex is set to -1
    <MenuContainer ref={ref} className={classNames} inert={!isOpen} top={headerTop} tabIndex={-1}>
      {/* The OrgPicker must be rendered outside of a scrollable container to ensure that it is not trapped in Safari, so don't render within MenuMainContent */}
      {showOrgPicker && <AccountingOrgsList />}

      <MenuMainContent>
        <MenuList hideMenu={hideMenu} />
        <MenuOtherLinks onItemClick={hideMenu} isShareUser={isShareUser} />
      </MenuMainContent>
    </MenuContainer>,
    modalRoot
  )
}

const MenuList: React.FC<{
  hideMenu: () => void
}> = ({ hideMenu }) => {
  const { currentPrimaryExperience: experience, hasAccountingFirmOrganization } =
    useSession<FrontendSession>()
  useEscapeKeyCapture(
    hideMenu,
    // needs to be nested within document to capture the escape event before DetailsView
    { target: document?.body }
  )

  // Even if you're currently looking at an operator dashboard (probably your own),
  // if you're an accountant, you should see the accountant menu
  // so you can switch to your clients
  if (hasAccountingFirmOrganization) {
    return <AccountantItems onItemClick={hideMenu} />
  }

  switch (experience) {
    case Experience.AccountantPortal:
      return <AccountantItems onItemClick={hideMenu} />

    case Experience.OperatorPortal:
    case Experience.ClientPortal:
      return <OperatorItems onItemClick={hideMenu} />
  }
}

function useShowOrgPicker() {
  const { currentPrimaryExperience, hasAccountingFirmOrganization } = useSession<FrontendSession>()

  return hasAccountingFirmOrganization || currentPrimaryExperience === Experience.AccountantPortal
}
